/**
 * Le plan de reprise, l'écartement, l'annulation et le scellement du mode
 * `?demo` (ARTO-132) : en mémoire, sans réseau, au-dessus des lots de
 * `demoReprise.ts`. Les refus de l'API sont rejoués avec leurs codes
 * (`docs/dev/api.md` § 12.18) : `plan_en_cours`, `rien_a_lancer`,
 * `plan_actif`, `lot_scelle`, `lot_non_execute`, `annulation_en_cours`,
 * `reprise_en_cours`, `rien_a_ecarter`, `lot_non_essaye`.
 *
 * Le plan de départ de la première galerie est à l'étape 3 : lieux, artistes
 * et œuvres sont faits, les images tournent, les contacts, les expositions et
 * les documents attendent. Aucune vraie galerie, aucune personne réelle.
 */
import { ApiError } from './api.js';
import { demoCorrespondance } from './demoCorrespondance.js';
import { compteDemoCourant } from './demoData.js';
import { demoReprise, type LotDemo } from './demoReprise.js';
import { exigerElevationDemo } from './demoSaisies.js';
import {
  ETAPE_DE_NATURE,
  planActif,
  type CibleRejets,
  type EtapePlan,
  type EtatPlan,
  type PlanVue,
  type RapportAnnulation,
  type ResultatScellement,
} from './plan.js';
import {
  exigeSecondFacteur,
  normaliserRapport,
  type LotReprise,
  type NatureLot,
} from './reprise.js';

interface PlanDemo {
  id: string;
  etat: EtatPlan;
  revision: number;
  lots: Array<{ lotId: string; nature: NatureLot; etape: number }>;
  arret: PlanVue['arret'];
  creeLe: string;
  termineLe: string | null;
}

const plans = new Map<string, PlanDemo | null>();
let compteur = 0;

function operateurCourant(): string {
  return compteDemoCourant().compte.email;
}

function conflit(raison: string, message: string): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison });
}

function introuvable(libelle: string): ApiError {
  return new ApiError(404, 'ressource.introuvable', `${libelle} introuvable.`);
}

/** Les lots du plan de départ, cités par leur identifiant stable. */
const LOTS_DU_PLAN_DE_DEPART: ReadonlyArray<[string, NatureLot]> = [
  ['demo-lot-plan-lieux', 'lieux'],
  ['demo-lot-plan-artistes', 'artistes'],
  ['demo-lot-plan-oeuvres', 'oeuvres'],
  ['demo-lot-plan-images', 'images'],
  ['demo-lot-plan-contacts', 'contacts'],
  ['demo-lot-plan-expositions', 'expositions'],
  ['demo-lot-plan-documents', 'documents'],
];

function planDe(idGalerie: string): PlanDemo | null {
  if (plans.has(idGalerie)) return plans.get(idGalerie) ?? null;
  const lots = demoReprise.interne(idGalerie);
  const depart = LOTS_DU_PLAN_DE_DEPART.every(([id]) => lots.some((lot) => lot.vue.id === id))
    ? {
        id: 'demo-plan-1',
        etat: 'en_cours' as const,
        revision: 3,
        lots: LOTS_DU_PLAN_DE_DEPART.map(([lotId, nature]) => ({
          lotId,
          nature,
          etape: ETAPE_DE_NATURE[nature],
        })),
        arret: null,
        creeLe: new Date(Date.now() - 2 * 3_600_000).toISOString(),
        termineLe: null,
      }
    : null;
  plans.set(idGalerie, depart);
  return depart;
}

function lotsDe(idGalerie: string): LotDemo[] {
  return demoReprise.interne(idGalerie);
}

function terminee(lot: LotDemo | undefined): boolean {
  return lot?.vue.etat === 'termine' || lot?.vue.etat === 'scelle';
}

/** Le plan avance à chaque lecture, comme le travail `reprise:planifier` de l'API. */
function faireAvancer(idGalerie: string, plan: PlanDemo): void {
  if (plan.etat !== 'en_cours') return;
  const lots = lotsDe(idGalerie);
  const lu = (id: string): LotDemo | undefined => lots.find((lot) => lot.vue.id === id);
  const echoue = plan.lots.filter((entree) => lu(entree.lotId)?.vue.etat === 'echoue');
  if (echoue.length > 0) {
    plan.etat = 'bloque';
    plan.arret = { raison: 'lots_en_echec', lots: echoue.map((entree) => entree.lotId) };
    plan.revision += 1;
    return;
  }
  const etapes = [...new Set(plan.lots.map((entree) => entree.etape))].sort((a, b) => a - b);
  for (const etape of etapes) {
    const deLEtape = plan.lots.filter((entree) => entree.etape === etape);
    if (deLEtape.every((entree) => terminee(lu(entree.lotId)))) continue;
    // Une nature à la fois : un lot prêt ne part que si aucun lot de sa nature ne tourne.
    for (const entree of deLEtape) {
      const lot = lu(entree.lotId);
      if (lot?.vue.etat !== 'pret') continue;
      const tourne = deLEtape.some((autre) => {
        const voisin = lu(autre.lotId);
        return (
          autre.nature === entree.nature &&
          (voisin?.vue.etat === 'enfile' || voisin?.vue.etat === 'en_cours')
        );
      });
      if (!tourne) demoReprise.demarrerImport(lot);
    }
    return;
  }
  plan.etat = 'termine';
  plan.termineLe = new Date().toISOString();
  plan.revision += 1;
}

function vue(idGalerie: string, plan: PlanDemo): PlanVue {
  faireAvancer(idGalerie, plan);
  const lots = lotsDe(idGalerie);
  const entrees = plan.lots.map((entree) => {
    const lot = lots.find((candidat) => candidat.vue.id === entree.lotId);
    return {
      id: entree.lotId,
      nature: entree.nature,
      etape: entree.etape,
      etat: lot?.vue.etat ?? null,
      morceau: null,
      compteurs: lot?.vue.compteurs ?? null,
    };
  });
  const etapes: EtapePlan[] = [...new Set(entrees.map((entree) => entree.etape))]
    .sort((a, b) => a - b)
    .map((rang) => {
      const deLEtape = entrees.filter((entree) => entree.etape === rang);
      const compter = (...etats: string[]): number =>
        deLEtape.filter((entree) => etats.includes(entree.etat ?? '')).length;
      return {
        etape: rang,
        natures: [...new Set(deLEtape.map((entree) => entree.nature))],
        lots: deLEtape.length,
        termines: compter('termine', 'scelle'),
        enCours: compter('enfile', 'en_cours'),
        echoues: compter('echoue'),
      };
    });
  const premiere = etapes.find((etape) => etape.termines < etape.lots);
  return {
    id: plan.id,
    galerieId: idGalerie,
    dossierId: null,
    etat: plan.etat,
    revision: plan.revision,
    etape: premiere?.etape ?? etapes[etapes.length - 1]?.etape ?? 1,
    etapes,
    lots: entrees,
    arret: plan.arret,
    operateur: 'exploitation@venio.test',
    travail:
      plan.etat === 'en_cours'
        ? {
            id: `${plan.id}-travail`,
            etat: 'en_attente',
            prochainPassage: new Date(Date.now() + 5_000).toISOString(),
          }
        : null,
    creeLe: plan.creeLe,
    termineLe: plan.termineLe,
  };
}

function trouverLot(idGalerie: string, lotId: string): LotDemo {
  const lot = lotsDe(idGalerie).find((candidat) => candidat.vue.id === lotId);
  if (!lot) {
    // Un lot de correspondance (et ses morceaux) vit dans un autre moteur de démonstration.
    if (demoCorrespondance.possede(idGalerie, lotId))
      throw conflit('lot_non_execute', 'Ce lot de démonstration ne s’annule pas ici.');
    throw introuvable('Lot');
  }
  return lot;
}

function copie(lot: LotDemo): LotReprise {
  return structuredClone(lot.vue);
}

/** Un import tourne ? Les lots figés de départ (« en file », « en cours ») ne comptent pas. */
function importEnCours(idGalerie: string): boolean {
  return lotsDe(idGalerie).some((lot) => lot.phase?.genre === 'import' || lot.defile !== null);
}

function annulationEnCours(idGalerie: string): boolean {
  return lotsDe(idGalerie).some(
    (lot) => lot.vue.annulation?.etat === 'demandee' || lot.vue.annulation?.etat === 'en_cours',
  );
}

interface RefusDemo {
  raison: string;
  message: string;
}

/**
 * Ce qui refuserait l'annulation du lot, dans l'ordre de l'API
 * (`refusAnnulationDuLot`, puis `refusAnnulationDeLaGalerie`) : la route de la
 * démonstration le jette, la fiche du lot le rend en clair. La démonstration
 * n'a pas de galerie « en service ».
 */
function refusAnnulation(idGalerie: string, lot: LotReprise): RefusDemo | null {
  if ((lot.morceaux?.liste.length ?? 0) > 0)
    return {
      raison: 'lot_de_correspondance',
      message:
        'Ce lot se découpe en morceaux : annulez ses morceaux, dans l’ordre inverse du plan.',
    };
  if (lot.etat === 'scelle' || (lot.scelleLe ?? null) !== null)
    return { raison: 'lot_scelle', message: 'Ce lot est scellé : il ne s’annule plus.' };
  if (lot.etat !== 'termine' && lot.etat !== 'echoue')
    return { raison: 'lot_non_execute', message: 'Seul un lot exécuté s’annule.' };
  if (lot.annulation?.etat === 'demandee' || lot.annulation?.etat === 'en_cours')
    return {
      raison: 'annulation_en_cours',
      message: 'L’annulation de ce lot est déjà en cours.',
    };
  if (importEnCours(idGalerie))
    return {
      raison: 'reprise_en_cours',
      message: 'Un import de reprise tourne dans cette galerie : attendez qu’il se termine.',
    };
  const plan = planDe(idGalerie);
  if (plan && planActif({ etat: plan.etat }))
    return {
      raison: 'plan_actif',
      message: 'Un plan de reprise occupe la galerie : abandonnez-le avant d’annuler un lot.',
    };
  return null;
}

export const demoPlan = {
  /** Les lots tels que l'API les rend : avec `annulable`, pour griser le bouton à l'avance. */
  avecAnnulable<T extends LotReprise>(idGalerie: string, lots: T[]): T[] {
    return lots.map((lot) => {
      const refus = refusAnnulation(idGalerie, lot);
      return { ...lot, annulable: { possible: refus === null, raison: refus?.raison ?? null } };
    });
  },

  plan(idGalerie: string): PlanVue | null {
    const plan = planDe(idGalerie);
    return plan ? vue(idGalerie, plan) : null;
  },

  lancer(idGalerie: string): PlanVue {
    const actuel = planDe(idGalerie);
    if (actuel && planActif({ etat: actuel.etat }))
      throw conflit('plan_en_cours', 'Un plan de reprise occupe déjà cette galerie.');
    if (annulationEnCours(idGalerie))
      throw conflit(
        'annulation_en_cours',
        'Une annulation de lot est en cours dans cette galerie : attendez qu’elle soit faite.',
      );
    const prets = lotsDe(idGalerie)
      .filter((lot) => lot.vue.etat === 'pret' && lot.vue.morceaux == null)
      .sort((a, b) => ETAPE_DE_NATURE[a.vue.nature] - ETAPE_DE_NATURE[b.vue.nature]);
    if (prets.length === 0)
      throw conflit('rien_a_lancer', 'Aucun lot prêt à lancer dans cette galerie.');
    if (prets.some((lot) => exigeSecondFacteur(lot.vue))) exigerElevationDemo();
    compteur += 1;
    const plan: PlanDemo = {
      id: `demo-plan-nouveau-${String(compteur)}`,
      etat: 'en_cours',
      revision: 0,
      lots: prets.map((lot) => ({
        lotId: lot.vue.id,
        nature: lot.vue.nature,
        etape: ETAPE_DE_NATURE[lot.vue.nature],
      })),
      arret: null,
      creeLe: new Date().toISOString(),
      termineLe: null,
    };
    plans.set(idGalerie, plan);
    return vue(idGalerie, plan);
  },

  geste(idGalerie: string, geste: 'pause' | 'reprendre' | 'abandonner'): PlanVue {
    const plan = planDe(idGalerie);
    const depuis: EtatPlan[] =
      geste === 'pause'
        ? ['en_cours']
        : geste === 'reprendre'
          ? ['en_pause', 'bloque']
          : ['en_cours', 'en_pause', 'bloque'];
    if (!plan || !depuis.includes(plan.etat))
      throw conflit(
        'plan_absent',
        geste === 'pause'
          ? 'Aucun plan en cours dans cette galerie.'
          : geste === 'reprendre'
            ? 'Aucun plan en pause ou bloqué dans cette galerie.'
            : 'Aucun plan actif dans cette galerie.',
      );
    if (geste === 'reprendre') {
      const restants = lotsDe(idGalerie).filter(
        (lot) => plan.lots.some((entree) => entree.lotId === lot.vue.id) && !terminee(lot),
      );
      if (restants.some((lot) => exigeSecondFacteur(lot.vue))) exigerElevationDemo();
    }
    if (geste === 'pause') plan.etat = 'en_pause';
    else if (geste === 'reprendre') {
      plan.etat = 'en_cours';
      plan.arret = null;
    } else {
      plan.etat = 'abandonne';
      plan.termineLe = new Date().toISOString();
      // Ce qui tournait finit : l'import de démonstration qui défilait se termine.
      for (const lot of lotsDe(idGalerie)) {
        if (lot.defile === null || !plan.lots.some((entree) => entree.lotId === lot.vue.id))
          continue;
        lot.defile = null;
        lot.vue.etat = 'termine';
        lot.vue.rapport = true;
        lot.vue.termineLe = new Date().toISOString();
        if (lot.vue.travail) {
          lot.vue.travail.etat = 'reussi';
          lot.vue.travail.avancement = null;
          lot.vue.travail.termineLe = lot.vue.termineLe;
        }
      }
    }
    plan.revision += 1;
    return vue(idGalerie, plan);
  },

  ecarter(idGalerie: string, lotId: string, motif: string): LotReprise {
    const lot = trouverLot(idGalerie, lotId);
    const net = motif.trim();
    if (net.length < 3 || net.length > 500)
      throw new ApiError(
        400,
        'validation.champ_invalide',
        'Le motif compte de 3 à 500 caractères.',
      );
    if (lot.vue.etat !== 'essaye')
      throw conflit(
        lot.vue.etat === 'pret' ? 'rien_a_ecarter' : 'lot_non_essaye',
        lot.vue.etat === 'pret'
          ? 'L’essai de ce lot n’a refusé aucune ligne : il n’y a rien à écarter.'
          : 'Ce lot doit avoir été essayé avant d’en écarter des lignes.',
      );
    const nombre = lot.vue.compteurs?.refuses ?? 0;
    if (nombre === 0) throw conflit('rien_a_ecarter', 'L’essai de ce lot n’a refusé aucune ligne.');
    lot.vue.etat = 'pret';
    lot.vue.ecartes = { nombre, motif: net, par: operateurCourant(), le: new Date().toISOString() };
    if (lot.vue.compteurs) lot.vue.compteurs = { ...lot.vue.compteurs, ecartes: nombre };
    lot.vue.revision += 1;
    return copie(lot);
  },

  /** Le CSV des rejets : la ligne, son libellé, ce qui la refuse (jamais le reste de la ligne). */
  rejets(idGalerie: string, lotId: string, _cible: CibleRejets): string {
    const lot = trouverLot(idGalerie, lotId);
    const erreurs = lot.essai ? normaliserRapport(structuredClone(lot.essai)).erreurs : [];
    const lignes =
      erreurs.length > 0
        ? erreurs.map((e) => [String(e.ligne), e.libelle, e.messages.join(' ')])
        : Array.from(
            { length: lot.vue.ecartes?.nombre ?? lot.vue.compteurs?.refuses ?? 0 },
            (_, i) => [String(5 + i * 11), '—', 'Une valeur obligatoire est vide.'],
          );
    return `\ufeff${[['Ligne', 'Libellé', 'Motif'], ...lignes].map((l) => l.join(';')).join('\r\n')}\r\n`;
  },

  annuler(idGalerie: string, lotId: string): LotReprise {
    // Le second facteur d'abord, comme l'API (garde de la route).
    exigerElevationDemo();
    const lot = trouverLot(idGalerie, lotId);
    const refus = refusAnnulation(idGalerie, lot.vue);
    if (refus) throw conflit(refus.raison, refus.message);
    const annulation = lot.vue.annulation;
    // Une annulation faite se rejoue sans rien changer au rapport de démonstration.
    if (annulation?.etat === 'faite') return demoPlan.avecAnnulable(idGalerie, [copie(lot)])[0]!;
    demoReprise.demarrerAnnulation(lot, operateurCourant());
    return demoPlan.avecAnnulable(idGalerie, [copie(lot)])[0]!;
  },

  rapportAnnulation(idGalerie: string, lotId: string): RapportAnnulation {
    const lot = trouverLot(idGalerie, lotId);
    if (!lot.rapportAnnulation) throw introuvable('Rapport d’annulation');
    return structuredClone(lot.rapportAnnulation);
  },

  sceller(idGalerie: string): ResultatScellement {
    exigerElevationDemo();
    const plan = planDe(idGalerie);
    if (plan && planActif({ etat: plan.etat }))
      throw conflit(
        'plan_actif',
        'Un plan de reprise occupe la galerie : terminez-le ou abandonnez-le avant de sceller.',
      );
    if (importEnCours(idGalerie))
      throw conflit('reprise_en_cours', 'Un import de reprise tourne encore dans cette galerie.');
    if (annulationEnCours(idGalerie))
      throw conflit(
        'annulation_en_cours',
        'Une annulation n’est pas faite : relancez-la jusqu’à son terme avant de sceller.',
      );
    const scelleLe = new Date().toISOString();
    let scelles = 0;
    for (const lot of lotsDe(idGalerie)) {
      if (lot.vue.etat !== 'termine' && lot.vue.etat !== 'echoue') continue;
      lot.vue.etat = 'scelle';
      lot.vue.scelleLe = scelleLe;
      lot.vue.revision += 1;
      scelles += 1;
    }
    return { scelles, scelleLe, dossierId: null };
  },
};
